import { useEffect, useRef, useState } from 'react'
import { Link, Outlet, ScrollRestoration, useLocation, useMatches, useNavigation } from 'react-router'
import { LazyMotion, MotionConfig, domAnimation } from 'framer-motion'
import { Ban, FlaskConical, UserRoundPlus } from 'lucide-react'
import { NativeAppBridge, PushPrompt } from '../components/app/NativeApp.jsx'
import { Container } from '../components/ui/primitives.jsx'
import { useSession } from '../lib/session.jsx'
import { formatFull } from '../lib/time.js'
import { SiteFooter } from './SiteFooter.jsx'
import { SiteNav } from './SiteNav.jsx'

function NoticeBars() {
  const { user, meta } = useSession()
  const location = useLocation()
  const needsProfile = user && !user.profile && !user.ban && !['/welcome', '/signin'].includes(location.pathname)
  return (
    <>
      {meta?.appEnv === 'staging' && (
        <div className="bg-secondary-600 text-white">
          <Container className="flex items-center gap-2 py-1.5 text-xs font-medium">
            <FlaskConical aria-hidden="true" className="size-3.5" />
            Staging environment. Data here may be reset at any time.
          </Container>
        </div>
      )}
      {needsProfile && (
        <div className="border-b border-primary-500/25 bg-primary-500/10">
          <Container className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <UserRoundPlus aria-hidden="true" className="size-4 text-primary-300" />
            <span>Set up your player profile to create or join teams.</span>
            <Link
              to={`/welcome?next=${encodeURIComponent(location.pathname + location.search)}`}
              className="rounded font-semibold text-primary-300 underline-offset-2 hover:underline"
            >
              Set it up
            </Link>
          </Container>
        </div>
      )}
      {user?.ban && (
        <output className="block border-b border-red-500/30 bg-red-950/70 text-red-100">
          <Container className="flex items-start gap-2 py-2.5 text-sm">
            <Ban aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <p>
              <strong className="font-semibold">{user.ban.expiresAt ? `Your account is banned until ${formatFull(user.ban.expiresAt)}.` : 'Your account is banned.'}</strong>{' '}
              {user.ban.reason} You can still browse, but you can’t make changes.{' '}
              <Link to="/support/new?kind=appeal" className="rounded font-semibold underline underline-offset-2 hover:text-white">
                Appeal
              </Link>
            </p>
          </Container>
        </output>
      )}
    </>
  )
}

// A thin bar across the top while the next page opens. It only shows if
// opening takes more than a moment, then runs to the end and fades.
function RouteProgress() {
  const busy = useNavigation().state !== 'idle'
  const [phase, setPhase] = useState('idle')
  if (busy && phase !== 'busy') setPhase('busy')
  if (!busy && phase === 'busy') setPhase('done')
  useEffect(() => {
    if (phase !== 'done') return undefined
    const timer = setTimeout(() => setPhase('idle'), 500)
    return () => clearTimeout(timer)
  }, [phase])
  return <div aria-hidden="true" className="route-progress" data-phase={phase} />
}

// Each new page fades in. It doesn't move, so nothing shifts under the
// pointer. Switching tabs within a page doesn't count as a new page.
function PageTransition({ children }) {
  const ref = useRef(null)
  const leaf = useMatches().at(-1)
  const { tab: _tab, ...params } = leaf?.params ?? {}
  const page = `${leaf?.id}:${JSON.stringify(params)}`
  const shown = useRef(page)
  useEffect(() => {
    if (shown.current === page) return
    shown.current = page
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    ref.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'cubic-bezier(0.2, 0, 0, 1)' })
  }, [page])
  return <div ref={ref}>{children}</div>
}

export function AppShell() {
  return (
    <MotionConfig reducedMotion="user">
      <LazyMotion features={domAnimation} strict>
        <a
          href="#main"
          className="sr-only z-50 rounded-lg bg-primary-500 px-4 py-2 font-medium text-ink-dark focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
        >
          Skip to content
        </a>
        {/* With the page drawn edge to edge (viewport-fit=cover, as the iOS
            app shows it), keep content clear of the notch and home bar. */}
        <div aria-hidden="true" className="fixed inset-x-0 top-0 z-50 h-[env(safe-area-inset-top)] bg-page" />
        <div className="flex min-h-dvh flex-col pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]">
          <NoticeBars />
          <PushPrompt />
          <SiteNav />
          <main id="main" tabIndex={-1} className="flex-1 outline-none">
            <PageTransition>
              <Outlet />
            </PageTransition>
          </main>
          <SiteFooter />
        </div>
        <RouteProgress />
        <ScrollRestoration />
        <NativeAppBridge />
      </LazyMotion>
    </MotionConfig>
  )
}
